import { useEffect, useState } from 'react';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import { Chip, Menu, MenuItem, Tooltip, Typography } from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { QUERY_KEYS } from '@/constants';
import { SocietyService } from '@/services/societyService';
import { useAuthStore } from '@/store/authStore';
import { SocietyStatusBadge } from '@/components/society/SocietyStatusBadge';
import { resolveSocietyStatus } from '@/types/societyStatus';
import type { Society, TenantInfo } from '@/types/api';
import { useTranslation } from '@/i18n';

function societyToTenant(society: Society): TenantInfo {
  const id = society.id || society._id;
  return {
    id,
    _id: society._id || society.id,
    name: society.name,
    slug: society.slug,
    logo: society.logo,
    registrationNumber: society.registrationNumber,
  };
}

export function TenantSwitcher() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const tenant = useAuthStore((s) => s.tenant);
  const user = useAuthStore((s) => s.user);
  const setTenant = useAuthStore((s) => s.setTenant);
  const isSuperAdmin = useAuthStore((s) => s.isSuperAdmin());
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);

  const tenantId = tenant?.id || tenant?._id || user?.tenantId || '';
  const canListSocieties = isSuperAdmin || Boolean(user?.tenantId);

  const societiesQuery = useQuery({
    queryKey: QUERY_KEYS.societies,
    queryFn: async () => SocietyService.list({ page: 1, limit: 100 }),
    enabled: canListSocieties,
    staleTime: 60_000,
  });

  const societies = societiesQuery.data?.data || [];

  useEffect(() => {
    if (tenantId || !societies.length) return;
    // Society users match their assigned tenantId. Super Admin starts in Platform scope with no society selected.
    if (!isSuperAdmin && user?.tenantId) {
      const preferred = societies.find((s) => (s.id || s._id) === user.tenantId);
      if (preferred) {
        setTenant(societyToTenant(preferred));
      }
    }
  }, [tenantId, societies, user?.tenantId, isSuperAdmin, setTenant]);

  const onSelect = (society: Society) => {
    setTenant(societyToTenant(society));
    setAnchor(null);
    void queryClient.invalidateQueries();
  };

  const onClearSelection = () => {
    setTenant(null);
    setAnchor(null);
    void queryClient.invalidateQueries();
  };

  const label = tenant?.name || (isSuperAdmin ? t('common.platformNoSociety') : t('common.noSociety'));

  return (
    <>
      <Tooltip title={isSuperAdmin ? 'Switch inspected society or return to Platform Scope' : label}>
        <Chip
          icon={<BusinessOutlinedIcon />}
          label={label}
          variant="outlined"
          clickable={isSuperAdmin && societies.length > 0}
          onClick={
            isSuperAdmin
              ? (event) => setAnchor(event.currentTarget)
              : undefined
          }
          color={tenantId ? 'default' : 'primary'}
          sx={{
            display: { xs: 'none', sm: 'inline-flex' },
            maxWidth: 240,
            height: 36,
            borderRadius: 1,
            bgcolor: 'background.paper',
            fontWeight: 600,
          }}
        />
      </Tooltip>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        slotProps={{ paper: { sx: { maxHeight: 380, minWidth: 280, maxWidth: 360 } } }}
      >
        {isSuperAdmin && (
          <MenuItem
            selected={!tenantId}
            onClick={onClearSelection}
            sx={{ fontWeight: 700, color: 'primary.main', py: 1 }}
          >
            {t('common.platformNoSociety')}
          </MenuItem>
        )}
        {societies.map((society) => {
          const id = society.id || society._id;
          const status = resolveSocietyStatus(society as any);
          return (
            <MenuItem
              key={id}
              selected={id === tenantId}
              onClick={() => onSelect(society)}
              sx={{ py: 1, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 }}
            >
              <Typography variant="body2" fontWeight={600} noWrap sx={{ width: '100%' }}>
                {society.name}
              </Typography>
              <SocietyStatusBadge
                registrationStatus={status.registrationStatus}
                verificationStatus={status.verificationStatus}
                variant="row"
                size="small"
              />
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
}

export default TenantSwitcher;
